/**
 * Job Creation Hints
 *
 * This module manages temporary storage of recently created job dates
 * to enable automatic date filter adjustment in the scheduled jobs view.
 *
 * When a job is created outside the current date filter range, we store
 * its dates temporarily. The jobs layout then checks for these hints and
 * expands the date filters to include the new job.
 *
 * Hints expire after 2 minutes to prevent stale data from affecting
 * unrelated sessions while giving users enough time to navigate.
 */

const STORAGE_KEY = 'recentlyCreatedJobDates';
const CLEAR_DATE_FILTERS_KEY = 'clearDateFiltersOnNextJobsVisit';
const HINT_EXPIRY_MS = 2 * 60 * 1000; // 2 minutes

interface JobDateHint {
  /** Earliest scheduled start date across all created jobs (ISO string) */
  startDate: string | null;
  /** Latest scheduled end date across all created jobs (ISO string) */
  endDate: string | null;
  /** Timestamp when the hint was stored */
  timestamp: number;
}

interface CreatedJob {
  scheduledStartDate?: string | null;
  scheduledEndDate?: string | null;
}

/**
 * Store the scheduled dates of recently created jobs.
 * If multiple jobs are created, stores the earliest start and latest end.
 * If there are already stored hints that haven't expired, merges with them (expands the range).
 * If hints have expired or are invalid, starts fresh.
 *
 * @param jobs - Array of created jobs with optional scheduled dates
 * @param reset - If true, clears existing hints and starts fresh (default: false)
 */
export function storeCreatedJobDates(
  jobs: CreatedJob[],
  reset: boolean = false
): void {
  if (!jobs || jobs.length === 0) return;

  try {
    // Clear existing hints if reset is requested
    if (reset) {
      clearCreatedJobDates();
    }

    // Get any existing hints that haven't expired (unless we just reset)
    const existing = reset ? null : getRecentlyCreatedJobDates();

    let earliestStart: Date | null = existing?.startDate
      ? new Date(existing.startDate)
      : null;
    let latestEnd: Date | null = existing?.endDate
      ? new Date(existing.endDate)
      : null;

    // Process each job to find the date range
    for (const job of jobs) {
      if (job.scheduledStartDate) {
        const startDate = new Date(job.scheduledStartDate);
        if (!earliestStart || startDate < earliestStart) {
          earliestStart = startDate;
        }
      }
      if (job.scheduledEndDate) {
        const endDate = new Date(job.scheduledEndDate);
        if (!latestEnd || endDate > latestEnd) {
          latestEnd = endDate;
        }
      }
      // If only start date exists, use it for end date comparison too
      if (job.scheduledStartDate && !job.scheduledEndDate) {
        const startDate = new Date(job.scheduledStartDate);
        if (!latestEnd || startDate > latestEnd) {
          latestEnd = startDate;
        }
      }
    }

    // Only store if we have at least one date
    if (!earliestStart && !latestEnd) return;

    const hint: JobDateHint = {
      startDate: earliestStart?.toISOString() ?? null,
      endDate: latestEnd?.toISOString() ?? null,
      timestamp: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(hint));
  } catch (error) {
    // localStorage might be unavailable - fail silently
    console.warn('Failed to store job creation hint:', error);
  }
}

/**
 * Get recently created job dates if they haven't expired.
 * Returns null if no valid hints exist.
 * Automatically cleans up invalid or expired data.
 */
export function getRecentlyCreatedJobDates(): {
  startDate: string | null;
  endDate: string | null;
} | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    let hint: JobDateHint;
    try {
      hint = JSON.parse(stored);
    } catch {
      // Invalid JSON - clean up corrupted data
      clearCreatedJobDates();
      return null;
    }

    // Validate hint structure
    if (
      typeof hint !== 'object' ||
      hint === null ||
      typeof hint.timestamp !== 'number'
    ) {
      // Invalid structure - clean up corrupted data
      clearCreatedJobDates();
      return null;
    }

    // Check if hint has expired
    if (Date.now() - hint.timestamp > HINT_EXPIRY_MS) {
      clearCreatedJobDates();
      return null;
    }

    // Validate dates if present
    if (hint.startDate && typeof hint.startDate !== 'string') {
      clearCreatedJobDates();
      return null;
    }
    if (hint.endDate && typeof hint.endDate !== 'string') {
      clearCreatedJobDates();
      return null;
    }

    return {
      startDate: hint.startDate,
      endDate: hint.endDate,
    };
  } catch (error) {
    // localStorage unavailable - fail silently
    console.warn('Failed to read job creation hint:', error);
    // Attempt cleanup even on error
    try {
      clearCreatedJobDates();
    } catch {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Clear stored job creation hints.
 * Called after the hints have been consumed by the jobs layout.
 */
export function clearCreatedJobDates(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // localStorage might be unavailable - fail silently
    console.warn('Failed to clear job creation hint:', error);
  }
}

/**
 * Set a flag to clear date filters on the next visit to the jobs page.
 * Used by onboarding flow to ensure newly created jobs are visible
 * without needing to expand filters to specific dates.
 */
export function setClearDateFiltersOnNextJobsVisit(): void {
  try {
    localStorage.setItem(CLEAR_DATE_FILTERS_KEY, 'true');
  } catch (error) {
    console.warn('Failed to set clear date filters flag:', error);
  }
}

/**
 * Check if date filters should be cleared on this jobs page visit.
 * Returns true and clears the flag if it was set.
 */
export function shouldClearDateFiltersOnJobsVisit(): boolean {
  try {
    const shouldClear = localStorage.getItem(CLEAR_DATE_FILTERS_KEY) === 'true';
    if (shouldClear) {
      localStorage.removeItem(CLEAR_DATE_FILTERS_KEY);
    }
    return shouldClear;
  } catch (error) {
    console.warn('Failed to check clear date filters flag:', error);
    return false;
  }
}

/**
 * Expand date filters to include the given job dates.
 * Only expands if the job dates fall outside the current filter range.
 *
 * @param currentFilters - Current URLSearchParams with date filters
 * @param jobDates - The job dates to include
 * @param view - The current view ('scheduled' or 'completed')
 * @returns New URLSearchParams with expanded filters, or the original if no expansion needed
 */
export function expandDateFiltersToIncludeJobs(
  currentFilters: URLSearchParams,
  jobDates: { startDate: string | null; endDate: string | null },
  view: 'scheduled' | 'completed' = 'scheduled'
): URLSearchParams {
  // Determine which filter keys to use based on view
  const dateAfterKey =
    view === 'scheduled' ? 'scheduledDateAfter' : 'completedDateAfter';
  const dateBeforeKey =
    view === 'scheduled' ? 'scheduledDateBefore' : 'completedDateBefore';

  const currentAfter = currentFilters.get(dateAfterKey);
  const currentBefore = currentFilters.get(dateBeforeKey);

  // If no date filters are set, no expansion needed
  if (!currentAfter && !currentBefore) {
    return currentFilters;
  }

  const jobStart = jobDates.startDate ? new Date(jobDates.startDate) : null;
  const jobEnd = jobDates.endDate ? new Date(jobDates.endDate) : null;

  // Use the earliest job date for comparison
  const jobEarliestDate = jobStart ?? jobEnd;
  // Use the latest job date for comparison
  const jobLatestDate = jobEnd ?? jobStart;

  if (!jobEarliestDate && !jobLatestDate) {
    return currentFilters;
  }

  let needsExpansion = false;
  const newFilters = new URLSearchParams(currentFilters);

  // Check if job is before the current "after" filter
  if (currentAfter && jobEarliestDate) {
    const afterDate = new Date(currentAfter);
    if (jobEarliestDate < afterDate) {
      // Expand to include the job's earliest date
      newFilters.set(dateAfterKey, jobEarliestDate.toISOString());
      needsExpansion = true;
    }
  }

  // Check if job is after the current "before" filter
  if (currentBefore && jobLatestDate) {
    const beforeDate = new Date(currentBefore);
    if (jobLatestDate > beforeDate) {
      // Expand to include the job's latest date (add 1 day buffer for end-of-day)
      const expandedDate = new Date(jobLatestDate);
      expandedDate.setDate(expandedDate.getDate() + 1);
      newFilters.set(dateBeforeKey, expandedDate.toISOString());
      needsExpansion = true;
    }
  }

  return needsExpansion ? newFilters : currentFilters;
}
